import type { Awareness } from 'y-protocols/awareness.js';

export interface UserInfo {
  id?: string;
  name: string;
  avatar?: string | null;
}

type UserSelection = Array<Record<string, unknown>>;

/**
 * Viewport state for collaborative presentation sync.
 * Broadcast by presenter, consumed by followers.
 */
export interface ViewportState {
  /** Current zoom level, constrained by the active canvas viewport. */
  zoom: number;
  /** Viewport center X coordinate */
  centerX: number;
  /** Viewport center Y coordinate */
  centerY: number;
  /** Visible world rectangle. Used by followers to fit the same presenter view across viewport sizes. */
  visibleBounds?: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  /** Current frame index in presentation mode (0-based) */
  frameIndex?: number;
  /** Current frame ID for validation */
  frameId?: string;
}

/**
 * Presentation display settings.
 * Synced from presenter to followers.
 */
export interface PresentationSettings {
  /** Frame fills entire screen vs fits within bounds */
  fillScreen: boolean;
  /** Black vignette background around frame */
  blackBackground: boolean;
  /** Hide the presentation toolbar */
  hideToolbar: boolean;
}

/**
 * Presentation mode type.
 * - 'free': Continuous viewport sync (pan/zoom)
 * - 'frames': Frame-based navigation sync
 */
export type PresentationMode = 'free' | 'frames';

/**
 * Collaborative presentation state.
 * Only one user per document can be presenting at a time.
 */
export interface PresentationState {
  /** Whether this user is currently presenting */
  isPresenting: boolean;
  /** Current presentation mode - free viewport or frame-based */
  mode?: PresentationMode;
  /** Whether to show follower cursors (presenter-only control) */
  showFollowerCursors?: boolean;
  /** Presentation display settings */
  settings?: PresentationSettings;
}

export interface DatabaseRowFocusState {
  /** Rendering context: canvas/fullscreen/page. Keeps visual focus local to a UI surface. */
  scope: string;
  /** Database block id */
  databaseId: string;
  /** Data-view id within the database */
  viewId: string;
  /** Row/card/task id */
  rowId: string;
  /** Client timestamp for stale/future conflict handling */
  updatedAt: number;
}

export interface DatabaseCellPresenceState {
  /** Caret selection length inside a text-like cell, when editing. */
  caretLength?: number;
  /** Caret offset inside a text-like cell, when editing. */
  caretOffset?: number;
  /** Rendering context: parent page doc + database id + cell. Shared across UI surfaces. */
  scope: string;
  /** Database block id */
  databaseId: string;
  /**
   * Data-view id within the database. Left out when the sender has no table
   * view open; the cell is then marked in every view that shows the column.
   */
  viewId?: string;
  /** Row id */
  rowId: string;
  /** Column/property id */
  columnId: string;
  /** Whether the remote user is selecting or editing the cell */
  mode: 'selected' | 'editing';
  /** Client timestamp for stale/future conflict handling */
  updatedAt: number;
  /** Set when the cell is edited from outside a table view. */
  surface?: 'app-studio';
}

export interface DatabaseAreaSelectionPresenceState {
  /** Rendering context: parent page doc + database id + table area. */
  scope: string;
  /** Database block id */
  databaseId: string;
  /** Data-view id within the database */
  viewId: string;
  /** Selected row ids in current table view order. */
  rowIds: string[];
  /** Selected column/property ids in current table view order. */
  columnIds: string[];
  /** Client timestamp for stale/future conflict handling */
  updatedAt: number;
}

export type DatabaseCanvasUiWindowState = {
  propertyLimit?: number;
  propertyOffset?: number;
  rowLimit?: number;
  rowOffset?: number;
};

export type DatabaseCanvasUiTableViewState = {
  columnOrder?: readonly string[];
  columnWidths?: Readonly<Record<string, number>>;
};

export type DatabaseCanvasUiSearchState = {
  expanded: boolean;
  query: string;
};

export type DatabaseCanvasUiPresentationState =
  | {
      mode: 'summary';
      updatedAt?: number;
    }
  | {
      mode: 'live';
      viewId: string;
      viewport: {
        rowAnchor?: {
          offsetPx: number;
          offsetRatio: number;
          rowId: string;
        };
        scrollLeft: number;
        scrollTop: number;
        target?: string;
        targets?: Record<
          string,
          {
            rowAnchor?: {
              offsetPx: number;
              offsetRatio: number;
              rowId: string;
            };
            scrollLeft: number;
            scrollTop: number;
          }
        >;
      };
      updatedAt?: number;
    };

export interface DatabaseCanvasUiState {
  /** Rendering context: parent page doc + database id + canvas surface. */
  scope: string;
  /** Database block id */
  databaseId: string;
  /** Shared canvas presentation mode/view/scroll state. */
  presentation: DatabaseCanvasUiPresentationState;
  /** Shared large-data pagination/window state per view. */
  windows?: Record<string, DatabaseCanvasUiWindowState>;
  /** Shared table presentation state per view. */
  tableViews?: Record<string, DatabaseCanvasUiTableViewState>;
  /** Shared search expansion, query, and resulting row filter per view. */
  searches?: Record<string, DatabaseCanvasUiSearchState>;
  /** Client timestamp for last-writer-wins live UI state. */
  updatedAt: number;
}

export interface DatabaseFullscreenPresenceState {
  /** Rendering context: parent page doc + fullscreen surface. */
  scope: string;
  /** Database block id currently opened in fullscreen. */
  databaseId: string;
  /** Client timestamp for stale cleanup/debugging. */
  updatedAt: number;
}

export interface AttachmentMediaPresenceState {
  /** Rendering context: parent page doc. */
  scope: string;
  /** Attachment block id. */
  attachmentId: string;
  /** Blob/source id when available, used only as extra identity. */
  sourceId?: string;
  /** Human readable file name. */
  name: string;
  /** Local attachment media kind. */
  mediaKind: 'audio' | 'video';
  /** Last user-visible media action. */
  action: 'play' | 'pause' | 'seek' | 'rate' | 'volume' | 'time';
  /** Current media time in seconds at updatedAt. */
  currentTime: number;
  /** Known media duration in seconds. */
  duration?: number;
  /** Whether the publisher is currently playing. */
  playing: boolean;
  /** Whether the publisher's local media is muted. */
  muted: boolean;
  /** Playback rate at updatedAt. */
  playbackRate: number;
  /** Whether this event was published by a presenter/moderator. */
  moderated?: boolean;
  /** Client timestamp for last-writer-wins live UI state. */
  updatedAt: number;
}

export interface CanvasDragPreviewElementState {
  /** Canvas block or surface element id. */
  elementId: string;
  /** Element bound for this preview frame, in model coordinates. */
  xywh: string;
  /** Element rotation for this preview frame, in degrees. */
  rotate?: number;
}

export interface CanvasDragPreviewState {
  /** Unique per-drag id. */
  dragId: string;
  /** Current absolute bounds, also consumed by clients without move interpolation. */
  elements: CanvasDragPreviewElementState[];
  /** Shared effective model-space movement from the drag-start positions. */
  translation?: { x: number; y: number };
  /** Actual pointer before snapping/axis constraints, in the same movement frame. */
  cursor?: { x: number; y: number };
  /** Changes only when membership, rotation or live dimensions need a new basis. */
  basisVersion?: number;
  /** Receiver-side offset of a retained full snapshot, not the current movement. */
  basisTranslation?: { x: number; y: number };
  /** Fast lookup for interaction locks. */
  elementIds: string[];
  /** Preview operation. Only move may carry a shared translation. */
  operation: 'move' | 'resize' | 'rotate';
  /** Model-space cursor position at drag start. */
  startCursor: {
    x: number;
    y: number;
  };
  /** Dragging blocks interaction; settling keeps fixed final bounds visible. */
  status: 'dragging' | 'settling';
  /** Client timestamp for stale cleanup/debugging. */
  updatedAt: number;
}

/** A place in a database: a row, or one cell of it. */
export interface DatabasePlace {
  databaseId: string;
  rowId: string;
  columnId?: string;
}

/** Reads a place in a database from published state; anything else is no place. */
export function readDatabasePlace(value: unknown): DatabasePlace | undefined {
  const place = value as Partial<DatabasePlace> | null | undefined;
  if (typeof place?.databaseId !== 'string' || !place.databaseId)
    return undefined;
  if (typeof place.rowId !== 'string' || !place.rowId) return undefined;
  return {
    databaseId: place.databaseId,
    rowId: place.rowId,
    ...(typeof place.columnId === 'string' && place.columnId
      ? { columnId: place.columnId }
      : {}),
  };
}

/** A person's assistant while it works for them in this document. */
export interface AssistantPresenceState {
  /** Shown name, in the owner's words: "Assistant of <name>". */
  label: string;
  /**
   * What the assistant points at, marks and types into, in the shape of
   * `selectionV2`: one list of selection JSON per selection manager.
   */
  selections: Record<string, UserSelection>;
  /** The row or cell of a database the assistant works on, while it does. */
  databasePlace?: DatabasePlace;
}

/** Reads what a client published about its assistant; malformed state is absent. */
export function readAssistantPresence(
  state: { assistant?: unknown } | null | undefined
): AssistantPresenceState | null {
  const assistant = state?.assistant as Partial<AssistantPresenceState> | null;
  if (typeof assistant?.label !== 'string' || !assistant.label.trim())
    return null;
  const selections: Record<string, UserSelection> = {};
  const published: unknown = assistant.selections;
  if (published && typeof published === 'object') {
    for (const [manager, list] of Object.entries(published)) {
      if (!Array.isArray(list)) continue;
      selections[manager] = list.filter(
        (item): item is Record<string, unknown> =>
          !!item && typeof item === 'object' && typeof item.type === 'string'
      );
    }
  }
  const databasePlace = readDatabasePlace(assistant.databasePlace);
  return {
    label: assistant.label,
    selections,
    ...(databasePlace ? { databasePlace } : {}),
  };
}

/**
 * An assistant takes part next to the person it works for. Awareness client
 * ids are unsigned, so the negative range keys the assistants without clashes.
 */
export function assistantPresenceKey(clientId: number): number {
  return -clientId - 1;
}

/** Whether a presence key stands for an assistant instead of a person. */
export function isAssistantPresenceKey(presenceKey: number): boolean {
  return presenceKey < 0;
}

/** The awareness client behind a presence key, be it a person or an assistant. */
export function presenceOwnerClientId(presenceKey: number): number {
  return presenceKey < 0 ? -presenceKey - 1 : presenceKey;
}

/**
 * The name and picture a presence key is shown with. An assistant carries its
 * own name and no picture; colour and visibility follow its owner.
 */
export function readPresenceUser(
  states: ReadonlyMap<number, { user?: UserInfo; assistant?: unknown }>,
  presenceKey: number
): UserInfo | undefined {
  const state = states.get(presenceOwnerClientId(presenceKey));
  if (!isAssistantPresenceKey(presenceKey)) return state?.user;
  const assistant = readAssistantPresence(state);
  return assistant ? { name: assistant.label } : undefined;
}

/** Someone taking part in a document: a person, or the assistant of one. */
export interface PresenceActor {
  /** The presence key: a person's awareness client, or `assistantPresenceKey` of it. */
  key: number;
  kind: 'person' | 'assistant';
  name: string;
  /** A person's profile picture as their presence carries it. */
  avatar?: string | null;
  /** The awareness client the actor takes part through. */
  ownerClientId: number;
  /** The row or cell of a database the actor is at. */
  databasePlace?: DatabasePlace;
}

type PresenceActorSource = Partial<
  Pick<RawAwarenessState, 'user' | 'databaseCellPresence' | 'databaseRowFocus'>
> & { assistant?: unknown };

/**
 * Everyone taking part, read in one place: the person of each client, at the
 * cell they are in or else at the row they focus, and their assistant while
 * it works.
 */
export function readPresenceActors(
  states: ReadonlyMap<number, PresenceActorSource>
): PresenceActor[] {
  const actors: PresenceActor[] = [];
  for (const [clientId, state] of states) {
    if (typeof state.user?.name === 'string') {
      const databasePlace =
        readDatabasePlace(state.databaseCellPresence) ??
        readDatabasePlace(state.databaseRowFocus);
      actors.push({
        key: clientId,
        kind: 'person',
        name: state.user.name,
        ownerClientId: clientId,
        ...(state.user.avatar ? { avatar: state.user.avatar } : {}),
        ...(databasePlace ? { databasePlace } : {}),
      });
    }
    const assistant = readAssistantPresence(state);
    if (assistant) {
      actors.push({
        key: assistantPresenceKey(clientId),
        kind: 'assistant',
        name: assistant.label,
        ownerClientId: clientId,
        ...(assistant.databasePlace
          ? { databasePlace: assistant.databasePlace }
          : {}),
      });
    }
  }
  return actors;
}

/**
 * The element of an App page a person points at for everyone who has the
 * preview of that App Container open. Ephemeral; nothing is stored.
 */
export interface AppPreviewPointerState {
  appBaseId: string;
  routePath: string;
  artifactVersion: number;
  /** Child index per step from the body of the page down to the element. */
  path: number[];
  tag: string;
  updatedAt: number;
}

// Raw JSON state in awareness CRDT
export type RawAwarenessState = {
  user?: UserInfo;
  color?: string;
  selectionV2: Record<string, UserSelection>;
  /** Latest canvas cursor retained only for this live awareness session. */
  lastCanvasCursor?: { x: number; y: number; updatedAt: number; scope: string };
  /** Viewport state for presentation sync */
  viewport?: ViewportState;
  /** Collaborative presentation state */
  presentation?: PresentationState;
  /** Ephemeral database row/card/task focus for collaborative attention. */
  databaseRowFocus?: DatabaseRowFocusState;
  /** Ephemeral database cell presence shared between canvas and fullscreen. */
  databaseCellPresence?: DatabaseCellPresenceState;
  /** Ephemeral database multi-cell area selection presence. */
  databaseAreaSelectionPresence?: DatabaseAreaSelectionPresenceState;
  /** Ephemeral edgeless database canvas UI state shared through page awareness. */
  databaseCanvasUi?: DatabaseCanvasUiState;
  /** Ephemeral database fullscreen presence, shown as a non-selection badge. */
  databaseFullscreenPresence?: DatabaseFullscreenPresenceState;
  /** Ephemeral media control presence for attachment audio/video playback. */
  attachmentMediaPresence?: AttachmentMediaPresenceState;
  /** Ephemeral canvas drag preview in the existing presence session. */
  canvasDragPreview?: CanvasDragPreviewState;
  /** Receipt for a full movement basis; permits compact frames on this session. */
  canvasDragPreviewBasisAck?: Record<string, string>;
  /** The user's own assistant, visible to everyone while it works. */
  assistant?: AssistantPresenceState | null;
  /** Element of an App preview page the user points at for the others. */
  appPreviewPointer?: AppPreviewPointerState;
  /**
   * Since when the user's tab is out of sight (client time). The transport
   * sets it and takes the user out of everyone's awareness once it is too old.
   */
  hiddenSince?: number;
};

export interface AwarenessEvent {
  id: number;
  type: 'add' | 'update' | 'remove';
  state?: RawAwarenessState;
}

export class AwarenessStore {
  readonly awareness: Awareness;

  constructor(awareness: Awareness) {
    this.awareness = awareness;
    this.awareness.setLocalStateField('selectionV2', {});
  }

  destroy() {
    this.awareness.destroy();
  }

  getLocalSelection(
    selectionManagerId: string
  ): ReadonlyArray<Record<string, unknown>> {
    return (
      (this.awareness.getLocalState()?.selectionV2 ?? {})[selectionManagerId] ??
      []
    );
  }

  getStates(): Map<number, RawAwarenessState> {
    return this.awareness.getStates() as Map<number, RawAwarenessState>;
  }

  getLocalState(): RawAwarenessState {
    return this.awareness.getLocalState() as RawAwarenessState;
  }

  setLocalState(state: RawAwarenessState): void {
    this.awareness.setLocalState(state);
  }

  setLocalStateField<Field extends keyof RawAwarenessState>(
    field: Field,
    value: RawAwarenessState[Field]
  ): void {
    this.awareness.setLocalStateField(field, value);
  }

  setLocalSelection(selectionManagerId: string, selection: UserSelection) {
    const state = this.getLocalState();
    const selectionV2 = {
      ...(state?.selectionV2 ?? {}),
      [selectionManagerId]: selection,
    };
    const cursor = selection.find(
      item =>
        item.type === 'cursor' &&
        typeof item.x === 'number' &&
        Number.isFinite(item.x) &&
        typeof item.y === 'number' &&
        Number.isFinite(item.y)
    );
    if (
      cursor &&
      typeof cursor.x === 'number' &&
      typeof cursor.y === 'number'
    ) {
      // One existing awareness update; no extra transport or persisted cursor index.
      this.setLocalState({
        ...state,
        selectionV2,
        lastCanvasCursor: {
          x: cursor.x,
          y: cursor.y,
          scope: selectionManagerId,
          updatedAt: Date.now(),
        },
      });
    } else this.awareness.setLocalStateField('selectionV2', selectionV2);
  }
}
